import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  FieldGroup,
  getFieldIdsOfFieldGroup,
  getFullNameForFieldID,
} from '../../../../location-utils';
import { takeUntil } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { MatMenu } from '@angular/material/menu';
import { ColDef, ColumnState } from 'ag-grid-community';
import { LocationListLocalStoreService } from '../../../location-list-local-store.service';
import { cloneDeep } from 'lodash';
import { LocationListApiService } from '../../../location-list-api.service';
import { Dictionary } from '@ngrx/entity';
import { PushPipe } from '@ngrx/component';
import { MatIcon } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { MatCheckbox } from '@angular/material/checkbox';
import { CC_COLUMN_DEFS } from '../../../../cc-list/cc-list-constants';

@Component({
    selector: 'app-column-menu',
    templateUrl: './column-menu.component.html',
    styleUrls: ['./column-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        MatMenu,
        MatCheckbox,
        NgClass,
        FormsModule,
        MatIcon,
        PushPipe,
    ],
})
export class ColumnMenuComponent implements OnInit, OnDestroy {

  @ViewChild(MatMenu, { static: true }) menu: MatMenu;

  // local column checkbox state - used to get a list of columns
  // which visibility should be modified
  private viewModel: BehaviorSubject<Dictionary<boolean>> = new BehaviorSubject<{[p: string]: boolean}>({});
  viewModel$: Observable<Dictionary<boolean>> = this.viewModel.asObservable();

  private componentDestroyed: Subject<void> = new Subject<void>();
  private columnState: ColumnState[] = [];

  protected readonly columns = [
    [FieldGroup.DEFAULT_DISPLAY, FieldGroup.OPENING_HOURS],
    [FieldGroup.ADDRESS, FieldGroup.MEDIA],
    [FieldGroup.OTHER, FieldGroup.PLACE_ACTION_LINKS],
    [FieldGroup.GMB_SPECIFIC, FieldGroup.BLS_SPECIFIC],
  ];

  fieldGroup = FieldGroup; // for HTML use

  constructor(private locationListLocalStore: LocationListLocalStoreService,
              private locationListApi: LocationListApiService) {
  }

  ngOnInit(): void {
    this.locationListLocalStore.columnStateArray$
        .pipe(takeUntil(this.componentDestroyed))
        .subscribe((cs) => {
          this.columnState = cloneDeep(cs);
          this.syncViewModel();
        });
  }

  ngOnDestroy() {
    this.componentDestroyed.next();
  }

  /**
   * Sync view model state
   */
  syncViewModel() {
    const nextValue: Dictionary<boolean> = {};

    this.columnState.forEach((cs) => {
      nextValue[cs.colId] = !cs.hide;
    });

    this.viewModel.next(nextValue);
  }

  /**
   * Returns a list of IDs of columns that can be displayed in the address list table
   */
  getAllFieldIds(): string[] {
    return Object.keys(this.viewModel.value);
  }

  /**
   * Generate new column state and update it.
   * @private
   */
  private updateColumnState() {
    this.getAllFieldIds().forEach(fieldId => {
      const columnState = this.columnState.find(cs => cs.colId === fieldId);
      // update column visibility based on local checkbox state
      columnState.hide = !this.viewModel.value[fieldId];
    });
    this.locationListApi.setColumnState(this.columnState);
  }


  /**
   * Update view model and column state.
   */
  columnCheckboxOnChange(fieldId: string, checked: boolean) {
    const nextState: Dictionary<boolean> = {...this.viewModel.value};
    nextState[fieldId] = checked;
    this.viewModel.next(nextState);
    this.updateColumnState();
  }

  /**
   * Get the IDs of all columns within a column group
   * @param group
   */
  getColumnGroupIds(group: FieldGroup) {
    return getFieldIdsOfFieldGroup(group, this.getAllFieldIds());
  }

  /**
   * Toggle every column within the respective group and update view model and column state.
   * @param group
   */
  columnGroupCheckboxOnChange(group: FieldGroup) {
    let colGrpIds: string[] = this.getColumnGroupIds(group);

    // determine checkboxes' next state - if all are presently checked, uncheck all,
    // otherwise check all
    const allSubfieldsChecked = this.allChecked(group);
    const nextCheckboxState: boolean = !allSubfieldsChecked;
    const nextViewModelState: Dictionary<boolean> = {...this.viewModel.value};

    colGrpIds.forEach(id => nextViewModelState[id] = nextCheckboxState);

    this.viewModel.next(nextViewModelState);
    this.updateColumnState();
  }

  /**
   * Returns a "pretty" name for a field ID
   * @param fieldId
   */
  getFieldNameByFieldID(fieldId: string): string {
    return getFullNameForFieldID(fieldId);
  }

  /**
   * Checks whether all columns within a fieldGroup are checked.
   * @param group
   */
  allChecked(group: FieldGroup): boolean {
    return this.getColumnGroupIds(group).every(id => this.getVisibleColumnsIds().includes(id));
  }

  /**
   * Checks whether some columns within a fieldGroup are checked.
   * @param group
   */
  someChecked(group: FieldGroup): boolean {
    return this.getColumnGroupIds(group).some(id => this.getVisibleColumnsIds().includes(id));
  }

  /**
   * Gets a list of IDs of currently visible columns
   * @private
   */
  private getVisibleColumnsIds(): string[] {
    return this.getAllFieldIds().filter(id => this.viewModel.value[id]);
  }

  /**
   * Used to mark menu items with an icon denoting that the marked column's visibility setting
   * is mirrored by the cc list table.
   * @param fieldId
   */
  visibilityMirroredInCC(fieldId: string) {
    return CC_COLUMN_DEFS.map((colDef: ColDef) => colDef.colId).includes(fieldId);
  }

}
